import { useMutation, useQuery } from '@tanstack/react-query';
import type {
    DataTag,
    DefinedInitialDataOptions,
    DefinedUseQueryResult,
    MutationFunction,
    QueryClient,
    QueryFunction,
    QueryKey,
    UndefinedInitialDataOptions,
    UseMutationOptions,
    UseMutationResult,
    UseQueryOptions,
    UseQueryResult,
} from '@tanstack/react-query';

import { useCallback } from 'react';

import type {
    CreateOrUpdateTenantModel,
    GetAllLoginProvidersResponse,
    GetLoginProviderByIdResponse,
    GetLoginProvidersByTenantIdParams,
    GetLoginProvidersByTenantIdResponse,
    GetLoginProvidersParams,
    GetTenantByIdParams,
    GetTenantByIdResponse,
    GetTenantDetailParams,
    GetTenantDetailsByIdResponse,
    GetTenantUsersActivityLogParams,
    GetTenantsParams,
    GetTenantsResponse,
    GetUserActivityResponse,
    ProblemDetails,
    SearchTenantsParams,
    TenantConfigurationModel,
    UpdateLoginDomainModel,
} from '../models';

import { useCustomIDSClient } from '../../custom-client';
import type { ErrorType } from '../../custom-client';

/**
 * Returns a list of tenants<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/tenants.read
 */
export const useGetTenantsHook = () => {
    const getTenants = useCustomIDSClient<GetTenantsResponse>();

    return useCallback(
        (params?: GetTenantsParams, signal?: AbortSignal) => {
            return getTenants({ url: `/tenants`, method: 'GET', params, signal });
        },
        [getTenants],
    );
};

export const getGetTenantsQueryKey = (params?: GetTenantsParams) => {
    return [`/tenants`, ...(params ? [params] : [])] as const;
};

export const useGetTenantsQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useGetTenantsHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params?: GetTenantsParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetTenantsHook>>>, TError, TData>>;
    },
) => {
    const { query: queryOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getGetTenantsQueryKey(params);

    const getTenants = useGetTenantsHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetTenantsHook>>>> = ({ signal }) =>
        getTenants(params, signal);

    return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetTenantsHook>>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetTenantsQueryResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useGetTenantsHook>>>>;
export type GetTenantsQueryError = ErrorType<ProblemDetails>;

export function useGetTenants<
    TData = Awaited<ReturnType<ReturnType<typeof useGetTenantsHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params: undefined | GetTenantsParams,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetTenantsHook>>>, TError, TData>>
            & Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetTenantsHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetTenantsHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetTenants<
    TData = Awaited<ReturnType<ReturnType<typeof useGetTenantsHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params?: GetTenantsParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetTenantsHook>>>, TError, TData>>
            & Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetTenantsHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetTenantsHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetTenants<
    TData = Awaited<ReturnType<ReturnType<typeof useGetTenantsHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params?: GetTenantsParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetTenantsHook>>>, TError, TData>>;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

export function useGetTenants<
    TData = Awaited<ReturnType<ReturnType<typeof useGetTenantsHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params?: GetTenantsParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetTenantsHook>>>, TError, TData>>;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useGetTenantsQueryOptions(params, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * Creates a new tenant<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/tenants.write
 */
export const useCreateTenantHook = () => {
    const createTenant = useCustomIDSClient<GetTenantByIdResponse>();

    return useCallback(
        (createOrUpdateTenantModel: CreateOrUpdateTenantModel, signal?: AbortSignal) => {
            return createTenant({
                url: `/tenants`,
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                data: createOrUpdateTenantModel,
                signal,
            });
        },
        [createTenant],
    );
};

export const useCreateTenantMutationOptions = <TError = ErrorType<ProblemDetails>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useCreateTenantHook>>>,
        TError,
        { data: CreateOrUpdateTenantModel },
        TContext
    >;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useCreateTenantHook>>>,
    TError,
    { data: CreateOrUpdateTenantModel },
    TContext
> => {
    const mutationKey = ['createTenant'];
    const { mutation: mutationOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey } };

    const createTenant = useCreateTenantHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useCreateTenantHook>>>,
        { data: CreateOrUpdateTenantModel }
    > = (props) => {
        const { data } = props ?? {};

        return createTenant(data);
    };

    return { mutationFn, ...mutationOptions };
};

export type CreateTenantMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useCreateTenantHook>>>>;
export type CreateTenantMutationBody = CreateOrUpdateTenantModel;
export type CreateTenantMutationError = ErrorType<ProblemDetails>;

export const useCreateTenant = <TError = ErrorType<ProblemDetails>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useCreateTenantHook>>>,
            TError,
            { data: CreateOrUpdateTenantModel },
            TContext
        >;
    },
    queryClient?: QueryClient,
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useCreateTenantHook>>>,
    TError,
    { data: CreateOrUpdateTenantModel },
    TContext
> => {
    return useMutation(useCreateTenantMutationOptions(options), queryClient);
};
/**
 * Returns the basic information of a tenant<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/tenants.read
 */
export const useGetTenantByIdHook = () => {
    const getTenantById = useCustomIDSClient<GetTenantByIdResponse>();

    return useCallback(
        (id: string, params?: GetTenantByIdParams, signal?: AbortSignal) => {
            return getTenantById({ url: `/tenants/${id}`, method: 'GET', params, signal });
        },
        [getTenantById],
    );
};

export const getGetTenantByIdQueryKey = (id: string, params?: GetTenantByIdParams) => {
    return [`/tenants/${id}`, ...(params ? [params] : [])] as const;
};

export const useGetTenantByIdQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useGetTenantByIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params?: GetTenantByIdParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetTenantByIdHook>>>, TError, TData>>;
    },
) => {
    const { query: queryOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getGetTenantByIdQueryKey(id, params);

    const getTenantById = useGetTenantByIdHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetTenantByIdHook>>>> = ({ signal }) =>
        getTenantById(id, params, signal);

    return { queryKey, queryFn, enabled: !!id, ...queryOptions } as UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetTenantByIdHook>>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetTenantByIdQueryResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useGetTenantByIdHook>>>>;
export type GetTenantByIdQueryError = ErrorType<ProblemDetails>;

export function useGetTenantById<
    TData = Awaited<ReturnType<ReturnType<typeof useGetTenantByIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params: undefined | GetTenantByIdParams,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetTenantByIdHook>>>, TError, TData>>
            & Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetTenantByIdHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetTenantByIdHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetTenantById<
    TData = Awaited<ReturnType<ReturnType<typeof useGetTenantByIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params?: GetTenantByIdParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetTenantByIdHook>>>, TError, TData>>
            & Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetTenantByIdHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetTenantByIdHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetTenantById<
    TData = Awaited<ReturnType<ReturnType<typeof useGetTenantByIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params?: GetTenantByIdParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetTenantByIdHook>>>, TError, TData>>;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

export function useGetTenantById<
    TData = Awaited<ReturnType<ReturnType<typeof useGetTenantByIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params?: GetTenantByIdParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetTenantByIdHook>>>, TError, TData>>;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useGetTenantByIdQueryOptions(id, params, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * Updates an existing tenant<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/tenants.write
 */
export const useUpdateTenantHook = () => {
    const updateTenant = useCustomIDSClient<GetTenantByIdResponse>();

    return useCallback(
        (id: string, createOrUpdateTenantModel: CreateOrUpdateTenantModel, signal?: AbortSignal) => {
            return updateTenant({
                url: `/tenants/${id}`,
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                data: createOrUpdateTenantModel,
                signal,
            });
        },
        [updateTenant],
    );
};

export const useUpdateTenantMutationOptions = <TError = ErrorType<ProblemDetails>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useUpdateTenantHook>>>,
        TError,
        { id: string; data: CreateOrUpdateTenantModel },
        TContext
    >;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useUpdateTenantHook>>>,
    TError,
    { id: string; data: CreateOrUpdateTenantModel },
    TContext
> => {
    const mutationKey = ['updateTenant'];
    const { mutation: mutationOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey } };

    const updateTenant = useUpdateTenantHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useUpdateTenantHook>>>,
        { id: string; data: CreateOrUpdateTenantModel }
    > = (props) => {
        const { id, data } = props ?? {};

        return updateTenant(id, data);
    };

    return { mutationFn, ...mutationOptions };
};

export type UpdateTenantMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useUpdateTenantHook>>>>;
export type UpdateTenantMutationBody = CreateOrUpdateTenantModel;
export type UpdateTenantMutationError = ErrorType<ProblemDetails>;

export const useUpdateTenant = <TError = ErrorType<ProblemDetails>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useUpdateTenantHook>>>,
            TError,
            { id: string; data: CreateOrUpdateTenantModel },
            TContext
        >;
    },
    queryClient?: QueryClient,
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useUpdateTenantHook>>>,
    TError,
    { id: string; data: CreateOrUpdateTenantModel },
    TContext
> => {
    return useMutation(useUpdateTenantMutationOptions(options), queryClient);
};
/**
 * Deletes an existing tenant<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/tenants.write
 */
export const useDeleteTenantHook = () => {
    const deleteTenant = useCustomIDSClient<void>();

    return useCallback(
        (id: string, signal?: AbortSignal) => {
            return deleteTenant({ url: `/tenants/${id}`, method: 'DELETE', signal });
        },
        [deleteTenant],
    );
};

export const useDeleteTenantMutationOptions = <TError = ErrorType<ProblemDetails>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useDeleteTenantHook>>>,
        TError,
        { id: string },
        TContext
    >;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useDeleteTenantHook>>>,
    TError,
    { id: string },
    TContext
> => {
    const mutationKey = ['deleteTenant'];
    const { mutation: mutationOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey } };

    const deleteTenant = useDeleteTenantHook();

    const mutationFn: MutationFunction<Awaited<ReturnType<ReturnType<typeof useDeleteTenantHook>>>, { id: string }> = (
        props,
    ) => {
        const { id } = props ?? {};

        return deleteTenant(id);
    };

    return { mutationFn, ...mutationOptions };
};

export type DeleteTenantMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useDeleteTenantHook>>>>;

export type DeleteTenantMutationError = ErrorType<ProblemDetails>;

export const useDeleteTenant = <TError = ErrorType<ProblemDetails>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useDeleteTenantHook>>>,
            TError,
            { id: string },
            TContext
        >;
    },
    queryClient?: QueryClient,
): UseMutationResult<Awaited<ReturnType<ReturnType<typeof useDeleteTenantHook>>>, TError, { id: string }, TContext> => {
    return useMutation(useDeleteTenantMutationOptions(options), queryClient);
};
/**
 * Removes an existing Tenant and assigns all Users to Umdasch World<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/tenants.write
 */
export const useResetTenantHook = () => {
    const resetTenant = useCustomIDSClient<void>();

    return useCallback(
        (id: string, signal?: AbortSignal) => {
            return resetTenant({ url: `/tenants/${id}`, method: 'POST', signal });
        },
        [resetTenant],
    );
};

export const useResetTenantMutationOptions = <TError = ErrorType<ProblemDetails>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useResetTenantHook>>>,
        TError,
        { id: string },
        TContext
    >;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useResetTenantHook>>>,
    TError,
    { id: string },
    TContext
> => {
    const mutationKey = ['resetTenant'];
    const { mutation: mutationOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey } };

    const resetTenant = useResetTenantHook();

    const mutationFn: MutationFunction<Awaited<ReturnType<ReturnType<typeof useResetTenantHook>>>, { id: string }> = (
        props,
    ) => {
        const { id } = props ?? {};

        return resetTenant(id);
    };

    return { mutationFn, ...mutationOptions };
};

export type ResetTenantMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useResetTenantHook>>>>;

export type ResetTenantMutationError = ErrorType<ProblemDetails>;

export const useResetTenant = <TError = ErrorType<ProblemDetails>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useResetTenantHook>>>,
            TError,
            { id: string },
            TContext
        >;
    },
    queryClient?: QueryClient,
): UseMutationResult<Awaited<ReturnType<ReturnType<typeof useResetTenantHook>>>, TError, { id: string }, TContext> => {
    return useMutation(useResetTenantMutationOptions(options), queryClient);
};
/**
 * Updates the configuration of an existing tenant<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/tenants.write
 */
export const useUpdateTenantConfigurationHook = () => {
    const updateTenantConfiguration = useCustomIDSClient<GetTenantDetailsByIdResponse>();

    return useCallback(
        (id: string, tenantConfigurationModel: TenantConfigurationModel, signal?: AbortSignal) => {
            return updateTenantConfiguration({
                url: `/tenants/${id}/configuration`,
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                data: tenantConfigurationModel,
                signal,
            });
        },
        [updateTenantConfiguration],
    );
};

export const useUpdateTenantConfigurationMutationOptions = <
    TError = ErrorType<ProblemDetails>,
    TContext = unknown,
>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useUpdateTenantConfigurationHook>>>,
        TError,
        { id: string; data: TenantConfigurationModel },
        TContext
    >;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useUpdateTenantConfigurationHook>>>,
    TError,
    { id: string; data: TenantConfigurationModel },
    TContext
> => {
    const mutationKey = ['updateTenantConfiguration'];
    const { mutation: mutationOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey } };

    const updateTenantConfiguration = useUpdateTenantConfigurationHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useUpdateTenantConfigurationHook>>>,
        { id: string; data: TenantConfigurationModel }
    > = (props) => {
        const { id, data } = props ?? {};

        return updateTenantConfiguration(id, data);
    };

    return { mutationFn, ...mutationOptions };
};

export type UpdateTenantConfigurationMutationResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useUpdateTenantConfigurationHook>>>
>;
export type UpdateTenantConfigurationMutationBody = TenantConfigurationModel;
export type UpdateTenantConfigurationMutationError = ErrorType<ProblemDetails>;

export const useUpdateTenantConfiguration = <TError = ErrorType<ProblemDetails>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useUpdateTenantConfigurationHook>>>,
            TError,
            { id: string; data: TenantConfigurationModel },
            TContext
        >;
    },
    queryClient?: QueryClient,
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useUpdateTenantConfigurationHook>>>,
    TError,
    { id: string; data: TenantConfigurationModel },
    TContext
> => {
    return useMutation(useUpdateTenantConfigurationMutationOptions(options), queryClient);
};
/**
 * Returns the details of a tenant<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/tenants.read
 */
export const useGetTenantDetailHook = () => {
    const getTenantDetail = useCustomIDSClient<GetTenantDetailsByIdResponse>();

    return useCallback(
        (id: string, params?: GetTenantDetailParams, signal?: AbortSignal) => {
            return getTenantDetail({ url: `/tenants/${id}/detail`, method: 'GET', params, signal });
        },
        [getTenantDetail],
    );
};

export const getGetTenantDetailQueryKey = (id: string, params?: GetTenantDetailParams) => {
    return [`/tenants/${id}/detail`, ...(params ? [params] : [])] as const;
};

export const useGetTenantDetailQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useGetTenantDetailHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params?: GetTenantDetailParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetTenantDetailHook>>>, TError, TData>>;
    },
) => {
    const { query: queryOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getGetTenantDetailQueryKey(id, params);

    const getTenantDetail = useGetTenantDetailHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetTenantDetailHook>>>> = ({ signal }) =>
        getTenantDetail(id, params, signal);

    return { queryKey, queryFn, enabled: !!id, ...queryOptions } as UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetTenantDetailHook>>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetTenantDetailQueryResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useGetTenantDetailHook>>>>;
export type GetTenantDetailQueryError = ErrorType<ProblemDetails>;

export function useGetTenantDetail<
    TData = Awaited<ReturnType<ReturnType<typeof useGetTenantDetailHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params: undefined | GetTenantDetailParams,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetTenantDetailHook>>>, TError, TData>>
            & Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetTenantDetailHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetTenantDetailHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetTenantDetail<
    TData = Awaited<ReturnType<ReturnType<typeof useGetTenantDetailHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params?: GetTenantDetailParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetTenantDetailHook>>>, TError, TData>>
            & Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetTenantDetailHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetTenantDetailHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetTenantDetail<
    TData = Awaited<ReturnType<ReturnType<typeof useGetTenantDetailHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params?: GetTenantDetailParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetTenantDetailHook>>>, TError, TData>>;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

export function useGetTenantDetail<
    TData = Awaited<ReturnType<ReturnType<typeof useGetTenantDetailHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params?: GetTenantDetailParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetTenantDetailHook>>>, TError, TData>>;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useGetTenantDetailQueryOptions(id, params, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * Returns a list of login providers for a given tenant<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/tenants.read
 */
export const useGetLoginProvidersByTenantIdHook = () => {
    const getLoginProvidersByTenantId = useCustomIDSClient<GetLoginProvidersByTenantIdResponse>();

    return useCallback(
        (id: string, params?: GetLoginProvidersByTenantIdParams, signal?: AbortSignal) => {
            return getLoginProvidersByTenantId({ url: `/tenants/${id}/login-provider`, method: 'GET', params, signal });
        },
        [getLoginProvidersByTenantId],
    );
};

export const getGetLoginProvidersByTenantIdQueryKey = (id: string, params?: GetLoginProvidersByTenantIdParams) => {
    return [`/tenants/${id}/login-provider`, ...(params ? [params] : [])] as const;
};

export const useGetLoginProvidersByTenantIdQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useGetLoginProvidersByTenantIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params?: GetLoginProvidersByTenantIdParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetLoginProvidersByTenantIdHook>>>, TError, TData>
        >;
    },
) => {
    const { query: queryOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getGetLoginProvidersByTenantIdQueryKey(id, params);

    const getLoginProvidersByTenantId = useGetLoginProvidersByTenantIdHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetLoginProvidersByTenantIdHook>>>> = ({
        signal,
    }) => getLoginProvidersByTenantId(id, params, signal);

    return { queryKey, queryFn, enabled: !!id, ...queryOptions } as UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetLoginProvidersByTenantIdHook>>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetLoginProvidersByTenantIdQueryResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useGetLoginProvidersByTenantIdHook>>>
>;
export type GetLoginProvidersByTenantIdQueryError = ErrorType<ProblemDetails>;

export function useGetLoginProvidersByTenantId<
    TData = Awaited<ReturnType<ReturnType<typeof useGetLoginProvidersByTenantIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params: undefined | GetLoginProvidersByTenantIdParams,
    options: {
        query: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetLoginProvidersByTenantIdHook>>>, TError, TData>
        >
            & Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetLoginProvidersByTenantIdHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetLoginProvidersByTenantIdHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetLoginProvidersByTenantId<
    TData = Awaited<ReturnType<ReturnType<typeof useGetLoginProvidersByTenantIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params?: GetLoginProvidersByTenantIdParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetLoginProvidersByTenantIdHook>>>, TError, TData>
        >
            & Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetLoginProvidersByTenantIdHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetLoginProvidersByTenantIdHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetLoginProvidersByTenantId<
    TData = Awaited<ReturnType<ReturnType<typeof useGetLoginProvidersByTenantIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params?: GetLoginProvidersByTenantIdParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetLoginProvidersByTenantIdHook>>>, TError, TData>
        >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

export function useGetLoginProvidersByTenantId<
    TData = Awaited<ReturnType<ReturnType<typeof useGetLoginProvidersByTenantIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params?: GetLoginProvidersByTenantIdParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetLoginProvidersByTenantIdHook>>>, TError, TData>
        >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useGetLoginProvidersByTenantIdQueryOptions(id, params, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * Updates a login provider<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/tenants.write
 */
export const useUpdateLoginProviderHook = () => {
    const updateLoginProvider = useCustomIDSClient<void>();

    return useCallback(
        (id: string, updateLoginDomainModel: UpdateLoginDomainModel, signal?: AbortSignal) => {
            return updateLoginProvider({
                url: `/tenants/${id}/login-provider`,
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                data: updateLoginDomainModel,
                signal,
            });
        },
        [updateLoginProvider],
    );
};

export const useUpdateLoginProviderMutationOptions = <
    TError = ErrorType<ProblemDetails>,
    TContext = unknown,
>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useUpdateLoginProviderHook>>>,
        TError,
        { id: string; data: UpdateLoginDomainModel },
        TContext
    >;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useUpdateLoginProviderHook>>>,
    TError,
    { id: string; data: UpdateLoginDomainModel },
    TContext
> => {
    const mutationKey = ['updateLoginProvider'];
    const { mutation: mutationOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey } };

    const updateLoginProvider = useUpdateLoginProviderHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useUpdateLoginProviderHook>>>,
        { id: string; data: UpdateLoginDomainModel }
    > = (props) => {
        const { id, data } = props ?? {};

        return updateLoginProvider(id, data);
    };

    return { mutationFn, ...mutationOptions };
};

export type UpdateLoginProviderMutationResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useUpdateLoginProviderHook>>>
>;
export type UpdateLoginProviderMutationBody = UpdateLoginDomainModel;
export type UpdateLoginProviderMutationError = ErrorType<ProblemDetails>;

export const useUpdateLoginProvider = <TError = ErrorType<ProblemDetails>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useUpdateLoginProviderHook>>>,
            TError,
            { id: string; data: UpdateLoginDomainModel },
            TContext
        >;
    },
    queryClient?: QueryClient,
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useUpdateLoginProviderHook>>>,
    TError,
    { id: string; data: UpdateLoginDomainModel },
    TContext
> => {
    return useMutation(useUpdateLoginProviderMutationOptions(options), queryClient);
};
/**
 * Provides the user activity log for all users of a tenant<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/tenants.read
 */
export const useGetTenantUsersActivityLogHook = () => {
    const getTenantUsersActivityLog = useCustomIDSClient<GetUserActivityResponse>();

    return useCallback(
        (id: string, params?: GetTenantUsersActivityLogParams, signal?: AbortSignal) => {
            return getTenantUsersActivityLog({
                url: `/tenants/${id}/user-activity-log`,
                method: 'GET',
                params,
                signal,
            });
        },
        [getTenantUsersActivityLog],
    );
};

export const getGetTenantUsersActivityLogQueryKey = (id: string, params?: GetTenantUsersActivityLogParams) => {
    return [`/tenants/${id}/user-activity-log`, ...(params ? [params] : [])] as const;
};

export const useGetTenantUsersActivityLogQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useGetTenantUsersActivityLogHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params?: GetTenantUsersActivityLogParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetTenantUsersActivityLogHook>>>, TError, TData>
        >;
    },
) => {
    const { query: queryOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getGetTenantUsersActivityLogQueryKey(id, params);

    const getTenantUsersActivityLog = useGetTenantUsersActivityLogHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetTenantUsersActivityLogHook>>>> = ({
        signal,
    }) => getTenantUsersActivityLog(id, params, signal);

    return { queryKey, queryFn, enabled: !!id, ...queryOptions } as UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetTenantUsersActivityLogHook>>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetTenantUsersActivityLogQueryResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useGetTenantUsersActivityLogHook>>>
>;
export type GetTenantUsersActivityLogQueryError = ErrorType<ProblemDetails>;

export function useGetTenantUsersActivityLog<
    TData = Awaited<ReturnType<ReturnType<typeof useGetTenantUsersActivityLogHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params: undefined | GetTenantUsersActivityLogParams,
    options: {
        query: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetTenantUsersActivityLogHook>>>, TError, TData>
        >
            & Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetTenantUsersActivityLogHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetTenantUsersActivityLogHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetTenantUsersActivityLog<
    TData = Awaited<ReturnType<ReturnType<typeof useGetTenantUsersActivityLogHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params?: GetTenantUsersActivityLogParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetTenantUsersActivityLogHook>>>, TError, TData>
        >
            & Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetTenantUsersActivityLogHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetTenantUsersActivityLogHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetTenantUsersActivityLog<
    TData = Awaited<ReturnType<ReturnType<typeof useGetTenantUsersActivityLogHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params?: GetTenantUsersActivityLogParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetTenantUsersActivityLogHook>>>, TError, TData>
        >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

export function useGetTenantUsersActivityLog<
    TData = Awaited<ReturnType<ReturnType<typeof useGetTenantUsersActivityLogHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params?: GetTenantUsersActivityLogParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetTenantUsersActivityLogHook>>>, TError, TData>
        >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useGetTenantUsersActivityLogQueryOptions(id, params, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * Returns a list of login providers<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/tenants.read
 */
export const useGetLoginProvidersHook = () => {
    const getLoginProviders = useCustomIDSClient<GetAllLoginProvidersResponse>();

    return useCallback(
        (params?: GetLoginProvidersParams, signal?: AbortSignal) => {
            return getLoginProviders({ url: `/tenants/login-provider`, method: 'GET', params, signal });
        },
        [getLoginProviders],
    );
};

export const getGetLoginProvidersQueryKey = (params?: GetLoginProvidersParams) => {
    return [`/tenants/login-provider`, ...(params ? [params] : [])] as const;
};

export const useGetLoginProvidersQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useGetLoginProvidersHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params?: GetLoginProvidersParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetLoginProvidersHook>>>, TError, TData>
        >;
    },
) => {
    const { query: queryOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getGetLoginProvidersQueryKey(params);

    const getLoginProviders = useGetLoginProvidersHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetLoginProvidersHook>>>> = ({ signal }) =>
        getLoginProviders(params, signal);

    return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetLoginProvidersHook>>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetLoginProvidersQueryResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useGetLoginProvidersHook>>>
>;
export type GetLoginProvidersQueryError = ErrorType<ProblemDetails>;

export function useGetLoginProviders<
    TData = Awaited<ReturnType<ReturnType<typeof useGetLoginProvidersHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params: undefined | GetLoginProvidersParams,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetLoginProvidersHook>>>, TError, TData>>
            & Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetLoginProvidersHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetLoginProvidersHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetLoginProviders<
    TData = Awaited<ReturnType<ReturnType<typeof useGetLoginProvidersHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params?: GetLoginProvidersParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetLoginProvidersHook>>>, TError, TData>
        >
            & Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetLoginProvidersHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetLoginProvidersHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetLoginProviders<
    TData = Awaited<ReturnType<ReturnType<typeof useGetLoginProvidersHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params?: GetLoginProvidersParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetLoginProvidersHook>>>, TError, TData>
        >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

export function useGetLoginProviders<
    TData = Awaited<ReturnType<ReturnType<typeof useGetLoginProvidersHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params?: GetLoginProvidersParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetLoginProvidersHook>>>, TError, TData>
        >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useGetLoginProvidersQueryOptions(params, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * Returns the information of a login provider<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/tenants.read
 */
export const useGetLoginProviderByIdHook = () => {
    const getLoginProviderById = useCustomIDSClient<GetLoginProviderByIdResponse>();

    return useCallback(
        (id: string, signal?: AbortSignal) => {
            return getLoginProviderById({ url: `/tenants/login-provider/${id}`, method: 'GET', signal });
        },
        [getLoginProviderById],
    );
};

export const getGetLoginProviderByIdQueryKey = (id: string) => {
    return [`/tenants/login-provider/${id}`] as const;
};

export const useGetLoginProviderByIdQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useGetLoginProviderByIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetLoginProviderByIdHook>>>, TError, TData>
        >;
    },
) => {
    const { query: queryOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getGetLoginProviderByIdQueryKey(id);

    const getLoginProviderById = useGetLoginProviderByIdHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetLoginProviderByIdHook>>>> = ({ signal }) =>
        getLoginProviderById(id, signal);

    return { queryKey, queryFn, enabled: !!id, ...queryOptions } as UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetLoginProviderByIdHook>>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetLoginProviderByIdQueryResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useGetLoginProviderByIdHook>>>
>;
export type GetLoginProviderByIdQueryError = ErrorType<ProblemDetails>;

export function useGetLoginProviderById<
    TData = Awaited<ReturnType<ReturnType<typeof useGetLoginProviderByIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    options: {
        query: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetLoginProviderByIdHook>>>, TError, TData>
        >
            & Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetLoginProviderByIdHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetLoginProviderByIdHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetLoginProviderById<
    TData = Awaited<ReturnType<ReturnType<typeof useGetLoginProviderByIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetLoginProviderByIdHook>>>, TError, TData>
        >
            & Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetLoginProviderByIdHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetLoginProviderByIdHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetLoginProviderById<
    TData = Awaited<ReturnType<ReturnType<typeof useGetLoginProviderByIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetLoginProviderByIdHook>>>, TError, TData>
        >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

export function useGetLoginProviderById<
    TData = Awaited<ReturnType<ReturnType<typeof useGetLoginProviderByIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetLoginProviderByIdHook>>>, TError, TData>
        >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useGetLoginProviderByIdQueryOptions(id, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * Deletes a login provider<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/tenants.write
 */
export const useDeleteLoginProviderHook = () => {
    const deleteLoginProvider = useCustomIDSClient<void>();

    return useCallback(
        (id: string, signal?: AbortSignal) => {
            return deleteLoginProvider({ url: `/tenants/login-provider/${id}`, method: 'DELETE', signal });
        },
        [deleteLoginProvider],
    );
};

export const useDeleteLoginProviderMutationOptions = <
    TError = ErrorType<ProblemDetails>,
    TContext = unknown,
>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useDeleteLoginProviderHook>>>,
        TError,
        { id: string },
        TContext
    >;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useDeleteLoginProviderHook>>>,
    TError,
    { id: string },
    TContext
> => {
    const mutationKey = ['deleteLoginProvider'];
    const { mutation: mutationOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey } };

    const deleteLoginProvider = useDeleteLoginProviderHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useDeleteLoginProviderHook>>>,
        { id: string }
    > = (props) => {
        const { id } = props ?? {};

        return deleteLoginProvider(id);
    };

    return { mutationFn, ...mutationOptions };
};

export type DeleteLoginProviderMutationResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useDeleteLoginProviderHook>>>
>;

export type DeleteLoginProviderMutationError = ErrorType<ProblemDetails>;

export const useDeleteLoginProvider = <TError = ErrorType<ProblemDetails>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useDeleteLoginProviderHook>>>,
            TError,
            { id: string },
            TContext
        >;
    },
    queryClient?: QueryClient,
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useDeleteLoginProviderHook>>>,
    TError,
    { id: string },
    TContext
> => {
    return useMutation(useDeleteLoginProviderMutationOptions(options), queryClient);
};
/**
 * Returns a list of tenants, based on the given search term<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/tenants.read
 */
export const useSearchTenantsHook = () => {
    const searchTenants = useCustomIDSClient<GetTenantsResponse>();

    return useCallback(
        (params?: SearchTenantsParams, signal?: AbortSignal) => {
            return searchTenants({ url: `/tenants/search`, method: 'GET', params, signal });
        },
        [searchTenants],
    );
};

export const getSearchTenantsQueryKey = (params?: SearchTenantsParams) => {
    return [`/tenants/search`, ...(params ? [params] : [])] as const;
};

export const useSearchTenantsQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useSearchTenantsHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params?: SearchTenantsParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useSearchTenantsHook>>>, TError, TData>>;
    },
) => {
    const { query: queryOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getSearchTenantsQueryKey(params);

    const searchTenants = useSearchTenantsHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useSearchTenantsHook>>>> = ({ signal }) =>
        searchTenants(params, signal);

    return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useSearchTenantsHook>>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type SearchTenantsQueryResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useSearchTenantsHook>>>>;
export type SearchTenantsQueryError = ErrorType<ProblemDetails>;

export function useSearchTenants<
    TData = Awaited<ReturnType<ReturnType<typeof useSearchTenantsHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params: undefined | SearchTenantsParams,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useSearchTenantsHook>>>, TError, TData>>
            & Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useSearchTenantsHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useSearchTenantsHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useSearchTenants<
    TData = Awaited<ReturnType<ReturnType<typeof useSearchTenantsHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params?: SearchTenantsParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useSearchTenantsHook>>>, TError, TData>>
            & Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useSearchTenantsHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useSearchTenantsHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useSearchTenants<
    TData = Awaited<ReturnType<ReturnType<typeof useSearchTenantsHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params?: SearchTenantsParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useSearchTenantsHook>>>, TError, TData>>;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

export function useSearchTenants<
    TData = Awaited<ReturnType<ReturnType<typeof useSearchTenantsHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params?: SearchTenantsParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useSearchTenantsHook>>>, TError, TData>>;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useSearchTenantsQueryOptions(params, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return { ...query, queryKey: queryOptions.queryKey };
}
